import { useApi } from 'arcway/lib/client';
import { useSession } from '../hooks/use-session.js';
import { ArrowDown, ArrowRight, ArrowUpRight, AudioLines, CalendarClock, Check, ChevronDown, Globe2, Headphones, Mail, Download, Play, Plus, Rss, UploadCloud, Users } from 'lucide-react';
import { Head, Link } from 'arcway/lib/client';
import { useEffect, useRef, useState } from 'react';
import { Logo } from '#components/logo.jsx';
import { SkipLink } from '#components/skip-link.jsx';
import { BillingPeriodControl, PricingCards } from '#components/billing/pricing-cards.jsx';
import { Button } from '#components/ui/button.jsx';
const platforms = [['Apple Podcasts', 'apple-podcasts.png'], ['Spotify', 'spotify.png'], ['Pocket Casts', 'pocket-casts.svg'], ['Overcast', 'overcast.svg'], ['YouTube Music', 'youtube-music.svg'], ['Castbox', 'castbox.svg'], ['Any other podcast apps', null]];
const faqs = [['How does the 14-day free trial work?', 'Your one-time trial starts when you choose a plan and complete checkout. A card is required. Cancel from Billing settings before the trial ends to prevent renewal. Accounts that have previously had a subscription are not eligible for another trial.'], ['What does unlimited downloads mean?', 'There is no monthly cap on episode downloads or listens, and no extra charge when more people tune in. Your audience can grow without a bigger hosting bill.'], ['Can I move an existing podcast to Mono?', 'Yes. Start with your public RSS feed, review the show and eligible episodes, then import them with their media. Your current feed stays unchanged while you prepare to switch.'], ['How does my show get onto podcast apps?', 'Mono creates an RSS feed for your show. Submit it to Apple Podcasts, Spotify, and other listening apps. After that, publish new episodes in Mono and those apps can pick them up from your feed.'], ['Can I use my own domain?', 'Yes. Every show can have a public podcast website on a Mono address. You can also connect your own domain, verify it with a DNS TXT record, and get automatic HTTPS. Domain allowances are shown in the plans above.'], ['Do I keep ownership of my podcast?', 'Yes. Your content is yours, and your podcast has a portable RSS feed. You can cancel your subscription from Billing settings. Published content stays online after the subscription ends, while creating new shows and adding domains requires an active plan.'], ['What can I upload?', 'Mono accepts MP3, M4A, WAV, and MP4 video files up to 5 GiB each. Add JPEG, PNG, or WebP artwork up to 20 MiB. Keep an episode as a draft, publish it now, or set a future publishing time.']];

// Original vector artwork: scalable, decorative, and no image download required.
function CoverArt({
  alternate = false
}) {
  return <svg aria-hidden='true' className='cover-art' viewBox='0 0 400 400' fill='none'>
      <rect width='400' height='400' fill={alternate ? '#b8c7e8' : '#eed1b7'} />
      <circle cx='295' cy='115' r='60' fill={alternate ? '#eaf294' : '#c85036'} />
      <path d='M0 330C95 150 150 150 260 330L300 400H0Z' fill={alternate ? '#455976' : '#697653'} />
      <path d='M100 400C180 210 270 200 400 275V400Z' fill={alternate ? '#273d4c' : '#303e31'} />
      <path d='M0 365C95 275 155 315 220 400H0Z' fill={alternate ? '#7188ab' : '#a5ad77'} />
      <path d='M258 260C235 300 258 350 220 400' stroke={alternate ? '#dce7ec' : '#eed1b7'} strokeWidth='3' />
      <circle cx='295' cy='115' r='78' stroke={alternate ? '#eaf294' : '#c85036'} strokeWidth='1' />
    </svg>;
}
function Waveform() {
  return <div aria-hidden='true' className='landing-waveform'>
      {[24, 38, 65, 40, 72, 90, 52, 32, 62, 84, 48, 96, 66, 40, 75, 54, 30, 60, 82, 45, 70, 34, 56, 26].map((height, index) => <span key={index} style={{
      height: `${height}%`
    }} />)}
    </div>;
}
function HeroIllustration() {
  return <figure className='landing-art' aria-label='Illustration of a podcast cover and an episode ready to publish'>
      <div aria-hidden='true' className='landing-art-stage'>
        <div className='art-orbit orbit-one' />
        <div className='art-orbit orbit-two' />
        <span className='art-sound'>
          <Headphones />
        </span>
        <span className='art-note'>
          A little curiosity.
          <br />A whole new audience.
        </span>
        <div className='art-record'>
          <span>
            mono
            <br />
            <small>33⅓ · SIDE A</small>
          </span>
        </div>
        <div className='art-cover'>
          <CoverArt />
          <div className='art-cover-type'>
            <span>AN INDEPENDENT PODCAST</span>
            <strong>
              Wandering
              <br />
              minds.
            </strong>
            <span>GOOD QUESTIONS. LONG CONVERSATIONS.</span>
          </div>
        </div>
        <div className='art-sticker'>
          <AudioLines />
          <span>
            Made to
            <br />
            be heard.
          </span>
        </div>
        <div className='art-player'>
          <span className='art-play'>
            <Play fill='currentColor' />
          </span>
          <div>
            <span className='art-player-label'>YOUR NEXT EPISODE</span>
            <strong>Follow your curiosity</strong>
            <Waveform />
          </div>
          <span className='art-ready'>
            <Check /> Ready
          </span>
        </div>
      </div>
      <figcaption className='sr-only'>
        Imaginary show. Real possibilities.
      </figcaption>
    </figure>;
}
function PublishingPreview() {
  return <figure className='publishing-preview'>
      <div className='preview-top'>
        <span>
          <AudioLines aria-hidden='true' /> Your workspace
        </span>
        <span>Episode 012</span>
      </div>
      <div className='preview-body'>
        <div className='preview-heading'>
          <div>
            <span className='landing-eyebrow'>WANDERING MINDS</span>
            <h3>One more good question.</h3>
          </div>
          <span className='preview-draft'>Draft</span>
        </div>
        <div className='preview-upload'>
          <UploadCloud aria-hidden='true' />
          <div>
            <strong>episode-012.mp3</strong>
            <span>Uploaded and ready</span>
          </div>
          <Check aria-hidden='true' />
        </div>
        <div className='preview-note'>
          <span>EPISODE NOTES</span>
          <p>
            A conversation about taking the scenic route, finding your people,
            and making room for a new idea.
          </p>
        </div>
        <div className='preview-schedule'>
          <CalendarClock aria-hidden='true' />
          <span>Publish on your schedule</span>
          <span className='preview-date'>Oct 12 · 09:00</span>
        </div>
      </div>
      <figcaption className='sr-only'>
        <span className='status-dot' /> An illustrative publishing workflow
      </figcaption>
    </figure>;
}
function SitePreview() {
  return <figure className='site-preview'>
      <div className='site-preview-address'>
        <Globe2 aria-hidden='true' /> thescenicroute.fm{' '}
        <span>Podcast website</span>
      </div>
      <div className='site-preview-content'>
        <div className='site-cover'>
          <CoverArt alternate />
        </div>
        <span className='landing-eyebrow'>A PODCAST FOR THE CURIOUS</span>
        <h3>The scenic route.</h3>
        <p>Conversations worth getting lost in.</p>
        <div className='site-preview-links'>
          <span>
            <Headphones aria-hidden='true' /> Listen
          </span>
          <span>
            <Rss aria-hidden='true' /> RSS feed
          </span>
        </div>
        <div className='site-episode'>
          <span>012</span>
          <strong>There’s more than one way there</strong>
          <Play aria-hidden='true' />
        </div>
        <div className='site-episode'>
          <span>011</span>
          <strong>A good place to begin</strong>
          <Play aria-hidden='true' />
        </div>
      </div>
      <figcaption className='sr-only'>Illustrative show and website</figcaption>
    </figure>;
}
function AnalyticsPreview() {
  return <figure className='analytics-preview'>
      <div className='preview-top'>
        <span>
          <AudioLines aria-hidden='true' /> Show analytics
        </span>
        <span>Wandering Minds</span>
      </div>
      <div className='analytics-preview-body'>
        <div className='analytics-preview-period'>
          <span>Wandering Minds</span>
          <span>Last 30 days</span>
        </div>
        <div className='analytics-preview-totals'>
          <div>
            <span>Downloads</span>
            <strong>2,480</strong>
          </div>
          <div>
            <span>Listeners</span>
            <strong>1,620</strong>
          </div>
        </div>
        <div className='analytics-preview-chart' aria-hidden='true'>
          <div className='chart-grid'>
            <span />
            <span />
            <span />
          </div>
          {[24, 40, 32, 55, 43, 37, 60, 48, 70, 51, 42, 67, 56, 82, 62, 73, 57, 88, 69, 79, 63, 94, 72, 84].map((height, index) => <span className='chart-bar' key={index} style={{
          height: `${height}%`
        }} />)}
        </div>
        <div className='chart-axis' aria-hidden='true'>
          <span>Sep 1</span>
          <span>Sep 15</span>
          <span>Sep 30</span>
        </div>
        <div className='analytics-preview-legend'>
          <span />
          <span>Daily downloads</span>
        </div>
      </div>
      <figcaption className='sr-only'>
        Illustrative analytics with sample data
      </figcaption>
    </figure>;
}
function TeamPreview() {
  return <figure className='team-preview'>
      <div className='preview-top'>
        <span>
          <Users aria-hidden='true' /> Your account team
        </span>
        <span>My Org</span>
      </div>
      <div className='team-preview-body'>
        <div className='team-preview-heading'>
          <div>
            <h3>Better, together.</h3>
            <p>A shared space for your shows.</p>
          </div>
          <span>
            <Users aria-hidden='true' />
          </span>
        </div>
        <div className='team-preview-person'>
          <span className='team-avatar avatar-olive'>JD</span>
          <div>
            <strong>Jamie Davis</strong>
            <span>You</span>
          </div>
          <span className='team-role'>Owner</span>
        </div>
        <div className='team-preview-person'>
          <span className='team-avatar avatar-blue'>AK</span>
          <div>
            <strong>Alex Kim</strong>
            <span>On the team</span>
          </div>
          <span className='team-role'>Member</span>
        </div>
        <div className='team-preview-person'>
          <span className='team-avatar avatar-peach'>SR</span>
          <div>
            <strong>Sam Rivera</strong>
            <span>On the team</span>
          </div>
          <span className='team-role'>Member</span>
        </div>
        <div className='team-preview-invite'>
          <Mail aria-hidden='true' />
          <span>Invite team member</span>
          <Plus aria-hidden='true' />
        </div>
      </div>
      <figcaption className='sr-only'>
        Illustrative account with fictional collaborators
      </figcaption>
    </figure>;
}
function FlowConnector({
  id
}) {
  return <svg aria-hidden='true' className='flow-connector' viewBox='0 0 100 120' preserveAspectRatio='none'>
      <defs>
        <marker id={id} markerWidth='5' markerHeight='5' refX='4' refY='2.5' orient='auto'>
          <path d='M0 0L5 2.5L0 5Z' fill='currentColor' />
        </marker>
      </defs>
      <path className='flow-path' d='M2 60H93' fill='none' stroke='currentColor' strokeWidth='1.5' strokeDasharray='4 5' markerEnd={`url(#${id})`} />
    </svg>;
}
function DistributionVisual() {
  const figureRef = useRef(null);
  const [flowActive, setFlowActive] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setFlowActive(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.25
    });
    observer.observe(figureRef.current);
    return () => observer.disconnect();
  }, []);
  return <section className='landing-distribution' aria-labelledby='distribution-title'>
      <div className='landing-wrap'>
        <p className='landing-eyebrow'>YOU MAKE IT. LET THE WORLD FIND IT.</p>
        <h2 id='distribution-title'>One RSS feed. Wherever they listen.</h2>
        <figure ref={figureRef} className='distribution-figure' data-flow-active={flowActive}>
          <div className='distribution-graph'>
            <div className='distribution-show'>
              <div className='distribution-cover'>
                <CoverArt />
                <strong>
                  Wandering
                  <br />
                  minds.
                </strong>
              </div>
              <span>Your podcast</span>
            </div>
            <FlowConnector id='upload-arrow' />
            <div className='distribution-hub'>
              <div>
                <Logo />
                <AudioLines aria-hidden='true' />
              </div>
              <span>Hosted on Mono</span>
            </div>
            <FlowConnector id='listening-arrow' />
            <svg aria-hidden='true' className='distribution-mobile-flow' viewBox='0 0 100 80' preserveAspectRatio='none'>
              <defs>
                <marker id='mobile-listening-arrow' markerWidth='5' markerHeight='5' refX='4' refY='2.5' orient='auto'>
                  <path d='M0 0L5 2.5L0 5Z' fill='currentColor' />
                </marker>
              </defs>
              <path className='flow-path' d='M80 0V10Q80 20 70 20H60Q50 20 50 30V74' fill='none' stroke='currentColor' strokeWidth='1' strokeDasharray='3 4' markerEnd='url(#mobile-listening-arrow)' />
            </svg>
            <ul className='landing-platforms'>
              {platforms.map(([name, file]) => <li key={name} title={name} className={!file ? 'platform-anywhere' : undefined}>
                  <span className='platform-icon'>
                    {file ? <img src={`/images/platforms/${file}`} width='32' height='32' alt={name} loading='lazy' /> : <Rss aria-hidden='true' />}
                  </span>
                  {!file && <span>{name}</span>}
                </li>)}
            </ul>
          </div>
          <figcaption>
            Submit your RSS feed to listening apps. They pick up new episodes
            from your feed.
          </figcaption>
        </figure>
      </div>
    </section>;
}
function MigrationVisual() {
  return <figure className='migration-visual'>
      <div className='migration-sources'>
        <div className='migration-host host-transistor'>
          <img src='/images/hosts/transistor.svg' alt='Transistor' width='130' height='34' loading='lazy' />
        </div>
        <div className='migration-host host-captivate'>
          <img src='/images/hosts/captivate.png' alt='Captivate' width='130' height='29' loading='lazy' />
        </div>
        <div className='migration-host host-spotify'>
          <img src='/images/platforms/spotify.png' alt='' width='24' height='24' loading='lazy' />
          <span>Spotify</span>
        </div>
        <div className='migration-host host-rss'>
          <Rss aria-hidden='true' />
          <span>Any public RSS feed</span>
        </div>
      </div>
      <svg aria-hidden='true' className='migration-connections' viewBox='0 0 100 180' preserveAspectRatio='none'>
        <path d='M0 20H25Q40 20 40 40V70Q40 90 60 90H95M0 65H25Q40 65 40 90H95M0 115H25Q40 115 40 90H95M0 160H25Q40 160 40 140V110Q40 90 60 90H95' fill='none' stroke='currentColor' strokeWidth='1.5' strokeDasharray='4 5' />
        <path d='M86 84L96 90L86 96' fill='none' stroke='currentColor' strokeWidth='1.5' />
      </svg>
      <div className='migration-home'>
        <Logo />
        <div className='migration-show'>
          <CoverArt />
          <div>
            <strong>Wandering Minds</strong>
            <span>Your next chapter</span>
          </div>
        </div>
        <div className='migration-included'>
          <Check aria-hidden='true' /> Your show details
        </div>
        <div className='migration-included'>
          <Check aria-hidden='true' /> Episodes &amp; media
        </div>
        <div className='migration-included'>
          <Check aria-hidden='true' /> A new home
        </div>
      </div>
      <figcaption className='sr-only'>
        Import a public RSS feed from a podcast host into Mono. Eligible
        episodes and media are included.
      </figcaption>
    </figure>;
}
function UnlimitedDownloads() {
  return <section className='landing-unlimited' id='unlimited-downloads' aria-labelledby='unlimited-title'>
      <div className='landing-wrap'>
        <div className='unlimited-intro'>
          <div>
            <p className='landing-eyebrow'>
              BIG AUDIENCE ENERGY. NO BIGGER BILL.
            </p>
            <h2 id='unlimited-title'>
              More listeners.
              <br />
              <em>Same hosting bill.</em>
            </h2>
            <p className='unlimited-description'>
              A great episode should bring you listeners, not an upgrade notice.
              Every Mono plan includes unlimited downloads and listens. Keep
              giving people a reason to press play.
            </p>
            <a className='landing-text-link' href='#pricing'>
              Find your space on Mono <ArrowUpRight aria-hidden='true' />
            </a>
          </div>
          <figure className='unlimited-visual'>
            <svg aria-hidden='true' viewBox='0 0 500 270'>
              <path className='infinity-road' d='M250 135C180 20 60 65 60 135S180 250 250 135S440 20 440 135S320 250 250 135' />
              <path className='infinity-lane' d='M250 135C180 20 60 65 60 135S180 250 250 135S440 20 440 135S320 250 250 135' />
            </svg>
            <span className='unlimited-listener listener-one' aria-hidden='true'>
              <Headphones />
            </span>
            <span className='unlimited-listener listener-two' aria-hidden='true'>
              <Play />
            </span>
            <span className='unlimited-listener listener-three' aria-hidden='true'>
              <Download />
            </span>
            <figcaption>
              Unlimited downloads.<span>ON EVERY MONO PLAN.</span>
            </figcaption>
          </figure>
        </div>
        <div className='download-comparison' role='group' aria-label='Monthly download allowances on selected entry plans'>
          <div>
            <span className='comparison-host'>
              Transistor <span>Starter</span>
            </span>
            <p>
              20,000 <span>/ month</span>
            </p>
          </div>
          <div>
            <span className='comparison-host'>
              Captivate <span>Personal</span>
            </span>
            <p>
              30,000 <span>/ month</span>
            </p>
          </div>
          <div className='comparison-mono'>
            <span className='comparison-host'>
              Mono <span>Every plan</span>
            </span>
            <p>
              Unlimited <span aria-hidden='true'>∞</span>
            </p>
            <span>No monthly download cap.</span>
          </div>
        </div>
        <p className='comparison-sources'>
          Monthly download allowances on selected entry plans, checked September
          5, 2026. Other features differ. These hosts offer higher allowances on
          higher plans; exceeding an allowance does not mean an automatic
          shutoff.
        </p>
      </div>
    </section>;
}
export default function HomePage({
  catalog = {
    products: []
  },
  catalogState = 'empty',
  developmentStylesheet
}) {
  const [billingPeriod, setBillingPeriod] = useState('month');
  const catalogAvailable = catalogState === 'available';
  const session = useSession();
  const billing = useApi('/account/billing', undefined, {
    disable: !session.authenticated || session.account?.role !== 'owner',
    ws: false
  });
  const trialEligible = !session.authenticated || billing.data?.trialEligible === true;
  return <>
      <Head description='Make something worth hearing. Host your podcast, schedule episodes, bring your RSS feed, and give every show a home with Mono.' title='Mono — Make something worth hearing.'>
        {developmentStylesheet && <link rel='stylesheet' href={developmentStylesheet} />}
        <meta content='website' property='og:type' />
        <meta content='mono.fm' property='og:site_name' />
      </Head>
      <div className='landing'>
        <SkipLink />
        <header className='landing-header landing-wrap'>
          <Link className='landing-logo' href='/' aria-label='Mono home'>
            <Logo />
          </Link>
          <nav aria-label='Main navigation' className='landing-navigation'>
            <a href='#features'>Why Mono</a>
            <a href='#pricing'>Pricing</a>
            <Link href='/import'>Move to Mono</Link>
            <Link href='/docs'>Docs</Link>
          </nav>
          <div className='landing-header-actions'>
            <Link href='/login'>
              Log in <ArrowUpRight aria-hidden='true' />
            </Link>
            <Button asChild>
              <Link href='/signup'>
                Get started <ArrowRight aria-hidden='true' />
              </Link>
            </Button>
          </div>
        </header>
        <main id='main-content' tabIndex='-1'>
          <section className='landing-hero landing-wrap'>
            <div className='landing-hero-copy'>
              <p className='landing-eyebrow'>
                <span className='status-dot' /> PODCAST HOSTING. FOR INDEPENDENT
                VOICES.
              </p>
              <h1>
                Make something <em>worth hearing.</em>
              </h1>
              <p className='landing-lead'>
                You make the show. We handle the hosting. Publish your podcast,
                find your people, and keep making the thing you love.
              </p>
              <div className='landing-hero-actions'>
                <Button asChild size='lg'>
                  <a href='#pricing'>
                    Find your plan <ArrowUpRight aria-hidden='true' />
                  </a>
                </Button>
                <Link className='landing-text-link' href='/import'>
                  Bring an existing show <ArrowRight aria-hidden='true' />
                </Link>
              </div>
              <p className='landing-small'>
                14-day trial with your first subscription. Your show stays
                yours.
              </p>
            </div>
            <HeroIllustration />
            <a className='landing-discover' href='#features'>
              <ArrowDown aria-hidden='true' /> A little less work. A lot more
              possibility.
            </a>
          </section>
          <DistributionVisual />
          <section id='features' className='landing-features landing-wrap'>
            <div className='landing-section-heading'>
              <div>
                <p className='landing-eyebrow'>
                  FROM “WHAT IF” TO “WE’RE LIVE”
                </p>
                <h2>
                  Less admin.
                  <br />
                  More airtime.
                </h2>
              </div>
              <p>
                You bring the voice. We bring the everyday tools to get it out
                into the world.
              </p>
            </div>
            <div className='landing-feature-grid'>
              <article className='landing-feature feature-publish'>
                <div className='feature-copy'>
                  <span className='feature-number'>01 / MAKE IT HAPPEN</span>
                  <h3>
                    Your next episode,
                    <br />
                    minus the busywork.
                  </h3>
                  <p>
                    Upload your media, add the story behind it, and publish. Or
                    leave it as a draft and schedule it for the right moment.
                  </p>
                </div>
                <PublishingPreview />
              </article>
              <article className='landing-feature feature-site'>
                <div className='feature-copy'>
                  <span className='feature-number'>02 / MAKE IT YOURS</span>
                  <h3>
                    A proper home.
                    <br />
                    For every show.
                  </h3>
                  <p>
                    Your episodes, artwork, and feed in a public podcast
                    website. Use a Mono address or your own verified domain with
                    automatic HTTPS.
                  </p>
                </div>
                <SitePreview />
              </article>
              <article className='landing-feature feature-analytics'>
                <div className='feature-copy'>
                  <span className='feature-number'>03 / FIND YOUR PEOPLE</span>
                  <h3>See the bigger picture.</h3>
                  <p>
                    Daily downloads and listeners, country breakdowns, and
                    episode milestones. See how your show is finding its
                    audience.
                  </p>
                </div>
                <AnalyticsPreview />
              </article>
              <article className='landing-feature feature-team'>
                <div className='feature-copy'>
                  <span className='feature-number'>04 / MAKE IT TOGETHER</span>
                  <h3>Bring your kind of people.</h3>
                  <p>
                    Invite collaborators to your account. Keep your shows and
                    episodes in one workspace, with owner and member roles.
                  </p>
                </div>
                <TeamPreview />
              </article>
            </div>
            <div className='landing-portable'>
              <span className='practical-icon'>
                <Rss aria-hidden='true' />
              </span>
              <div>
                <h3>Your voice. Your feed.</h3>
                <p>
                  Built on open RSS, so listeners choose their app. Unlimited
                  downloads &amp; listens, without a bill that grows with your
                  audience.
                </p>
              </div>
              <Link className='landing-text-link' href='/docs'>
                Explore the tools <ArrowUpRight aria-hidden='true' />
              </Link>
            </div>
          </section>
          <UnlimitedDownloads />
          <section className='landing-import' aria-labelledby='import-title'>
            <div className='landing-wrap landing-import-inner'>
              <div>
                <p className='landing-eyebrow'>ALREADY OUT IN THE WORLD?</p>
                <h2 id='import-title'>
                  Same show.
                  <br />
                  New home.
                </h2>
                <p>
                  Bring your RSS feed. Review your show and import eligible
                  episodes with their media. Your existing feed stays untouched
                  while you get settled.
                </p>
                <Button asChild size='lg'>
                  <Link href='/import'>
                    Import your feed <ArrowUpRight aria-hidden='true' />
                  </Link>
                </Button>
              </div>
              <MigrationVisual />
            </div>
          </section>
          <section id='pricing' className='landing-pricing landing-wrap'>
            <div className='landing-section-heading'>
              <div>
                <p className='landing-eyebrow'>
                  A SMALL BILL FOR YOUR BIG IDEA
                </p>
                <h2>
                  Room to grow.
                  <br />
                  No counting listens.
                </h2>
              </div>
              <div>
                <p>
                  Choose your space. Every plan includes unlimited episodes,
                  downloads, and collaborators.
                </p>
                <p className='landing-trial'>Start with a 14-day free trial.</p>
                <p className='landing-small'>
                  First subscription only. Card required. Cancel anytime.
                </p>
              </div>
            </div>
            {catalogAvailable ? <>
                <div className='landing-period'>
                  <BillingPeriodControl catalog={catalog} onChange={setBillingPeriod} value={billingPeriod} />
                  <span>All prices in USD</span>
                </div>
                <div className='landing-plan-cards'>
                  <PricingCards trialEligible={trialEligible} billingPeriod={billingPeriod} catalog={catalog} />
                </div>
              </> : <div className='landing-empty'>
                <h3>Pricing is not currently available</h3>
                <p>
                  You can still create an account. Current plans will appear
                  when checkout is available.
                </p>
                <div>
                  <Button asChild>
                    <Link href='/signup'>
                      Create your account <ArrowRight aria-hidden='true' />
                    </Link>
                  </Button>
                  <Link className='landing-text-link' href='/enterprise'>
                    Talk to us about Enterprise{' '}
                    <ArrowUpRight aria-hidden='true' />
                  </Link>
                </div>
              </div>}
            <p className='landing-pricing-note'>
              Need a hand choosing?{' '}
              <a href='mailto:support@mono.fm'>
                Email support@mono.fm <ArrowUpRight aria-hidden='true' />
              </a>
            </p>
          </section>
          <section id='faq' className='landing-faq'>
            <div className='landing-wrap landing-faq-grid'>
              <div>
                <p className='landing-eyebrow'>THE GOOD QUESTIONS</p>
                <h2>
                  Before you
                  <br />
                  press play.
                </h2>
                <p>
                  A few things you might be wondering.
                  <br />
                  For everything else,{' '}
                  <a href='mailto:support@mono.fm'>just ask us.</a>
                </p>
              </div>
              <div className='landing-questions'>
                {faqs.map(([question, answer]) => <details key={question}>
                    <summary>
                      {question}
                      <Plus aria-hidden='true' className='faq-plus' />
                      <ChevronDown aria-hidden='true' className='faq-minus' />
                    </summary>
                    <p>{answer}</p>
                  </details>)}
              </div>
            </div>
          </section>
          <section className='landing-closing'>
            <div className='landing-wrap'>
              <span className='closing-sound' aria-hidden='true'>
                <AudioLines />
              </span>
              <p className='landing-eyebrow'>
                THERE’S A SHOW ONLY YOU CAN MAKE.
              </p>
              <h2>Let’s hear it.</h2>
              <p>
                The conversation, the rabbit hole, the idea that won’t leave you
                alone.
                <br />
                Give it a home on Mono.
              </p>
              <Button asChild size='lg'>
                <Link href='/signup'>
                  Start your podcast <ArrowUpRight aria-hidden='true' />
                </Link>
              </Button>
              <Link className='closing-import' href='/import'>
                Or bring the one you’re already making{' '}
                <ArrowRight aria-hidden='true' />
              </Link>
            </div>
          </section>
        </main>
        <footer className='landing-footer landing-wrap'>
          <div className='landing-footer-top'>
            <Link href='/' aria-label='Mono home'>
              <Logo />
            </Link>
            <p>For the love of making something.</p>
            <a href='mailto:support@mono.fm'>
              Say hello <ArrowUpRight aria-hidden='true' />
            </a>
          </div>
          <div className='landing-footer-bottom'>
            <span>Independent voices, all welcome.</span>
            <nav aria-label='Footer navigation'>
              <Link href='/docs'>Docs</Link>
              <Link href='/enterprise'>Enterprise</Link>
              <Link href='/legal/privacy'>Privacy</Link>
              <Link href='/legal/terms'>Terms</Link>
              <a href='https://app.mono.fm/login'>Legacy login</a>
            </nav>
          </div>
        </footer>
      </div>
    </>;
}